import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatasetService } from '../../../../../shared/dataset/service/dataset.service';
import { StudyEditorContext } from '../../../../study-editor.context';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { StudyInstanceModel } from '../../../../../shared/dataset/model/study-instance.model';
import { FileDownloadHelper } from '../../../../../entities/sample/file-download.helper';
import { ModalConfirmComponent } from '../../../../../shared/modal/modal-confirm.component';
import { DatasetModel } from '../../../../../shared/dataset/model/dataset.model';
import { DATASET_TYPES_SUB_OBSERVATION_IDS } from '../../../../../shared/dataset/model/dataset-type.enum';

interface ExportFormat {
    name: string;
    extension: string;
    type: string;
}

@Component({
    selector: 'jhi-export-study-modal',
    templateUrl: './export-study-modal.component.html',
    styleUrl: './export-study-modal.component.scss'
})
export class ExportStudyModalComponent {

    @Input() isExperimentalDesignDetailsExport: boolean = false;

    title: string;
    exportFormats: ExportFormat[] = [];
    selectedFormat: ExportFormat | null = null;
    includeSampleGenotypeValues: boolean = false;
    singleFile: boolean = true;
    transposeByPlot: boolean = false;
    collectionOrderId: number = 1;

    isEnvironmentsExport: boolean;
    isStudySettingsExport: boolean;
    datasetInstances: StudyInstanceItem[] = [];
    private datasetId: number;

    currentDataset: DatasetModel;
    selectAll: boolean = true;
    isExporting: boolean;

    constructor(
        public modalService: NgbModal,
        public activeModal: NgbActiveModal,
        private datasetService: DatasetService,
        private studyEditorContext: StudyEditorContext,
        private alertService: AlertService,
        private translateService: TranslateService,
        private fileDownloadHelper: FileDownloadHelper,
    ) {
    }

    ngOnInit(): void {
        this.title = this.translateService.instant('study-editor.study-dataset.export-study-modal.title');
        this.isEnvironmentsExport = this.studyEditorContext.getEnvironmentDataset()?.datasetId === this.datasetId;
        this.currentDataset = this.studyEditorContext.allDatasets.find((dataset) => dataset.datasetId === this.datasetId );
        this.isStudySettingsExport = this.studyEditorContext.studyDetails.id === this.datasetId;

        this.initExportFormats();

        this.datasetService.getDatasetInstances(this.studyEditorContext.studyId, this.datasetId).subscribe(datasetInstances => {
            if (datasetInstances) {
                this.datasetInstances = datasetInstances.map((instance: StudyInstanceModel) => {
                    return {
                        ...instance,
                        isChecked: true
                    };
                });
            }
        });

    }

    initExportFormats(): void {
        this.exportFormats = [
            { name: 'CSV', extension: '.csv', type: 'csv' },
            { name: 'Excel', extension: '.xlsx', type: 'xlsx' },
        ];
        if (!this.isEnvironmentsExport && !this.isStudySettingsExport) {
            this.exportFormats = [...this.exportFormats,
                { name: 'Fieldbook CSV', extension: '.csv', type: 'ksu_csv' },
                { name: 'Fieldbook Excel', extension: '.xlsx', type: 'ksu_xlsx' }];
        }
        this.selectedFormat = this.exportFormats[1];
    }

    backToDatasetOptionModal() {
        this.activeModal.dismiss('back');
    }

    doSelectAll() {
        this.datasetInstances.forEach((instance) => {
            instance.isChecked = this.selectAll;
        });
    }

    export() {

        if (!this.isStudySettingsExport) {
            const instanceIds = this.getSelectedInstanceIds();
            if (instanceIds.length === 0) {
                this.alertService.error('study-editor.study-dataset.export-study-modal.instance-table.select-one-environment');
                return;
            }
        }

        if (this.collectionOrderId !== 1) {
            this.checkIfInstancesHaveFieldMap();
        } else {
            this.submitExport();
        }
    }

    submitExport() {
        this.isExporting = true;
        this.singleFile = this.selectedFormat.name === 'CSV' ? this.singleFile : false;
        this.singleFile = this.isEnvironmentsExport ? true : this.singleFile;
        this.transposeByPlot = this.selectedFormat.name === 'Excel' ? this.transposeByPlot : false;
        const instanceIds = this.getSelectedInstanceIds();
        this.datasetService.exportDatasetNew(this.studyEditorContext.studyId, this.datasetId, instanceIds,
            this.collectionOrderId, this.singleFile, this.selectedFormat.type, this.includeSampleGenotypeValues,
            this.transposeByPlot, this.isExperimentalDesignDetailsExport).toPromise()
            .then((response) => {
                const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
                this.fileDownloadHelper.save(response.body, fileName);
                this.activeModal.close();
            }).finally(() => {
                this.isExporting = false;
            });

    };

    checkIfInstancesHaveFieldMap() {
        // If some of the selected instances don't have fieldmap, show the confirm popup.
        const someInstancesHaveNoFieldmap = this.datasetInstances.some((item) => {
            return !item.hasFieldLayout && item.isChecked;
        });

        if (someInstancesHaveNoFieldmap) {
            this.showConfirmModal();
        } else {
            this.submitExport();
        }
    };

    showConfirmModal() {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.export-study-modal.instance-table.selected-instances-have-field-plan');
        confirmModalRef.result.then(()=> {
            this.submitExport();
        });
    };

    getSelectedInstanceIds(): number[] {
        return this.datasetInstances.filter((instance) => instance.isChecked).map((instance) => instance.instanceId);
    }

    protected readonly DATASET_TYPES_SUB_OBSERVATION_IDS = DATASET_TYPES_SUB_OBSERVATION_IDS;

}

class StudyInstanceItem extends StudyInstanceModel {
    isChecked: boolean;
}
